import { motion } from "framer-motion";
import { useAppSettings } from "../../../components/providers/AppSettingsProvider";
import { getPageBackgroundGradient } from "../../../config/theme";

interface BackgroundLayerProps {
    conversationActive: boolean;
}

export function BackgroundLayer({ conversationActive }: BackgroundLayerProps) {
    const { theme } = useAppSettings();
    const isDark = theme === 'dark';

    return (
        <>
            {/* Background Gradient */}
            <div className={`absolute inset-0 bg-gradient-to-b ${getPageBackgroundGradient(theme)}`} />

            {/* Animated Squares */}
            <motion.div
                className="absolute -translate-x-1/2"
                style={{ width: '100%', height: '10%' }}
                initial={{ bottom: '0px' }}
                animate={{
                    bottom: conversationActive ? '30vh' : '0px',
                }}
                transition={{
                    duration: 0.5,
                    ease: "easeInOut"
                }}
            >
                {/* Rear Square */}
                <motion.div
                    style={{
                        position: 'absolute',
                        width: '200vw',
                        height: '200vw',
                        background: isDark ? 'rgba(88, 127, 255, 0.3)' : 'rgba(173, 216, 255, 0.4)',
                        borderRadius: '32%',
                        backdropFilter: 'blur(40px) saturate(100%)',
                        border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.3)',
                        boxShadow: isDark
                            ? '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.3)'
                            : '0 8px 32px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)',
                        willChange: 'transform',
                        transformOrigin: '50% 50%',
                        transform: 'translate(-50%, -50%)',
                    }}
                    animate={{ rotate: [30, 390] }}
                    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                />

                {/* Middle Square */}
                <motion.div
                    style={{
                        position: 'absolute',
                        width: '200vw',
                        height: '200vw',
                        background: isDark ? 'rgba(34, 78, 223, 0.3)' : 'rgba(135, 206, 250, 0.35)',
                        borderRadius: '32%',
                        backdropFilter: 'blur(10px) saturate(100%)',
                        border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.4)',
                        boxShadow: isDark
                            ? '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4)'
                            : '0 8px 32px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
                        willChange: 'transform',
                        transformOrigin: '50% 50%',
                        transform: 'translate(-50%, -50%)',
                    }}
                    animate={{
                        rotate: [10, 370]
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                />

                {/* Front Square */}
                <motion.div
                    style={{
                        position: 'absolute',
                        width: '200vw',
                        height: '200vw',
                        background: isDark ? 'rgba(164, 203, 255, 0.05)' : 'rgba(200, 230, 255, 0.3)',
                        borderRadius: '32%',
                        backdropFilter: 'blur(5px) saturate(100%)',
                        border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.5)',
                        boxShadow: isDark
                            ? '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
                            : '0 8px 32px rgba(0, 0, 0, 0.03), inset 0 1px 0 rgba(255, 255, 255, 0.7)',
                        willChange: 'transform',
                        transformOrigin: '50% 50%',
                        transform: 'translate(-50%, -50%)',
                    }}
                    animate={{
                        rotate: [0, 360]
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                />
            </motion.div>

            {/* Decorative Stars & Dots */}
            <div className="absolute top-60 right-12 z-0">
                <div className="relative">
                    <div className="absolute inset-0 bg-yellow-400/60 blur-lg animate-pulse" style={{ width: '60px', height: '60px', borderRadius: '50%' }} />
                    <img src="/images/star_4.png" alt="star" className="w-10 h-10 relative z-10 drop-shadow-[0_0_20px_rgba(255,215,0,0.8)]" />
                </div>
            </div>

            <div className="absolute top-3/4 right-10 z-0" style={{ transform: 'translateY(-50%)' }}>
                <div className="relative">
                    <div className="absolute inset-0 bg-yellow-400/50 blur-lg animate-pulse" style={{ width: '54px', height: '54px', borderRadius: '50%' }} />
                    <img src="/images/star_5.png" alt="star" className="w-9 h-9 relative z-10 drop-shadow-[0_0_20px_rgba(255,215,0,0.8)]" />
                </div>
            </div>

            <div className="absolute bottom-24 left-12 z-0">
                <div className="relative">
                    <div className="absolute inset-0 bg-yellow-400/40 blur-lg animate-pulse" style={{ width: '48px', height: '48px', borderRadius: '50%' }} />
                    <img src="/images/star_4.png" alt="star" className="w-8 h-8 relative z-10 drop-shadow-[0_0_20px_rgba(255,215,0,0.8)]" />
                </div>
            </div>

            <div className="absolute top-1/2 left-12 z-0" style={{ transform: 'translateY(-50%)' }}>
                <div className="w-6 h-6 rounded-full bg-white/80 blur-sm shadow-[0_0_20px_rgba(255,255,255,0.8)]" />
            </div>
        </>
    );
}
